import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth (session_id in hash), let AuthCallback handle it first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.needs_verification) return { needs_verification: true, email: data.email };
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    // Register now always returns needs_verification (OTP flow).
    if (data.needs_verification) return { needs_verification: true, email: data.email };
    setUser(data.user);
    return data.user;
  };

  const verifyOtp = async (email, code) => {
    const { data } = await api.post("/auth/verify-otp", { email, code });
    setUser(data.user);
    return data.user;
  };

  const requestPhoneOtp = async (countryCode, phoneNumber) => {
    const { data } = await api.post("/auth/phone/request-otp", { country_code: countryCode, phone_number: phoneNumber });
    return data;
  };

  const verifyPhoneOtp = async (countryCode, phoneNumber, code) => {
    const { data } = await api.post("/auth/phone/verify-otp", { country_code: countryCode, phone_number: phoneNumber, code });
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, verifyOtp, requestPhoneOtp, verifyPhoneOtp, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
